import { Form, Spin, Typography } from "antd";
import { FormInstance } from "antd/lib/form";
import AppLoader from "components/AppLoader";
import CustomCollapse from "components/CustomCollapse";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import _ from "lodash";
import React, {
  MutableRefObject,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import TicketsCreationAPI from "restAPI/TicketsCreationAPI";

import { CustomFieldV2, CustomFieldsV2 } from "../CustomFields/CustomFieldsV2";
import {
  CustomFieldStateMap,
  CustomFields,
  InitialValues,
} from "../CustomFields/types";
import {
  alterCustomFieldsAPIResponse,
  checkEmptyField,
  generateCustomFields,
  getCollapseProps,
  getNoCustomFieldsMessage,
} from "../CustomFields/utils";

type APIPramsRef = {
  [fieldName: string]: any;
};

type Props = {
  customFieldsValueRef: MutableRefObject<Map<string, any>>;
  useValueField?: boolean;
  form: FormInstance;
  loginConfigId: number;
  ticketConfigurationId?: number;
  initialValues: InitialValues;
  configId: number;
  changeMode: boolean;
  innerRef?: boolean;
  toolName: string;
  tooltip?: string;
  showFieldDefaultValue?: boolean;
  runbook?: boolean;
  onLoadingChange?: (loading: boolean) => void;
};

const ServiceNowCustomFields = forwardRef(
  (
    {
      customFieldsValueRef,
      useValueField = false,
      form,
      loginConfigId,
      ticketConfigurationId,
      initialValues,
      configId,
      changeMode,
      innerRef = false,
      toolName,
      tooltip,
      showFieldDefaultValue = true,
      runbook = false,
      onLoadingChange,
    }: Props,
    ref,
  ) => {
    const initialData = useRef(initialValues);
    const [inputCustomFields, setInputCustomFields] = useState<CustomFields>(
      [],
    );
    const customFieldStateRef = useRef<CustomFieldStateMap>({});
    const [loading, setLoading] = useState(false);
    const apiParamRef = useRef<APIPramsRef>({});
    const thisRef = useRef(innerRef);
    const featureFlags = useFeatureFlag();

    const text = useMemo(() => {
      if (loading) {
        return;
      }
      return (
        <Typography.Text strong>
          {getNoCustomFieldsMessage(
            initialValues,
            loginConfigId,
            ticketConfigurationId,
          )}
        </Typography.Text>
      );
    }, [loginConfigId, ticketConfigurationId, initialValues, loading]);

    const resetFields = useCallback(() => {
      setInputCustomFields([]);
      customFieldStateRef.current = {};
      apiParamRef.current = {};
    }, []);

    const onValuesChange = useCallback(
      (current) => {
        if (customFieldsValueRef) {
          customFieldsValueRef.current = {
            ...customFieldsValueRef.current,
            ...current,
          };
        }
        _.forEach(current, (value, key) => {
          const newState = {
            ...customFieldStateRef.current,
            [key]: _.isBoolean(value) ? value : !_.isEmpty(value),
          };
          customFieldStateRef.current = newState;
        });
      },
      [customFieldsValueRef],
    );

    useEffect(() => {
      onLoadingChange?.(loading);
    }, [loading, onLoadingChange]);

    useEffect(() => {
      if (!checkEmptyField(loginConfigId)) {
        const { projectKey, issueType, issueTypeId } = initialValues;
        if (projectKey && issueType) {
          setInputCustomFields([]);
          setLoading(true);
          let isEdit = changeMode;
          if (isEdit && !thisRef.current) {
            thisRef.current = true;
            isEdit = false;
          }
          if (!issueType) {
            return;
          }
          // pass configId if fetching existing custom fields config
          // pass loginConfigId, issueType and project key if issueType/projectKey is changed or not in edit mode
          const params =
            !configId ||
            (isEdit &&
              (issueType !== initialData.current?.issueType ||
                projectKey !== initialData.current?.projectKey))
              ? {
                  loginConfigId,
                  projectKey,
                  issueType,
                  ...(issueTypeId ? { issueTypeId } : {}),
                }
              : { configId };
          const { promise, cancel } =
            TicketsCreationAPI.getCustomTicketFields(params);
          promise
            .then(({ fields }: { fields: CustomFields }) => {
              const { customState, fields: modifiedFields } =
                alterCustomFieldsAPIResponse({
                  fields,
                  form,
                  initialData,
                  useValueField,
                  customFieldsValueRef,
                  featureFlags,
                  innerRef,
                  toolName,
                  projectKey,
                  showFieldDefaultValue,
                });
              customFieldStateRef.current = customState;
              setInputCustomFields(modifiedFields);
            })
            .finally(() => setLoading(false));
          return cancel;
        } else {
          setLoading(false);
          resetFields();
        }
      }
    }, [
      customFieldsValueRef,
      loginConfigId,
      ticketConfigurationId,
      form,
      initialValues,
      setInputCustomFields,
      apiParamRef,
      changeMode,
      configId,
      resetFields,
      innerRef,
      useValueField,
      toolName,
      featureFlags.TICKETING_CUSTOM_SEVERITY_RISK_RATING,
      featureFlags,
      showFieldDefaultValue,
    ]);

    const getApiParams = useCallback(
      (
        loginConfigId: number,
        projectKey: string,
        fieldLabel: string,
        type?: string,
      ) => {
        const newApiParams = { loginConfigId, projectKey, fieldLabel, type };
        if (!_.isEqual(apiParamRef.current[fieldLabel], newApiParams)) {
          apiParamRef.current[fieldLabel] = newApiParams;
        }
        return apiParamRef.current[fieldLabel];
      },
      [],
    );

    const customField: Array<CustomFieldV2> = useMemo(() => {
      return generateCustomFields({
        fields: inputCustomFields,
        projectKey: initialValues.projectKey,
        loginConfigId,
        initialValues,
        featureFlags,
        customFieldStateRef,
        getApiParams,
      });
    }, [
      inputCustomFields,
      initialValues,
      loginConfigId,
      featureFlags,
      getApiParams,
    ]);

    const renderFields = useCallback(
      (customFields) => {
        return (
          <CustomFieldsV2
            customFields={customFields}
            form={form}
            onValuesChange={onValuesChange}
            toolName={toolName}
            runbook={runbook}
            initialValues={initialData.current}
            customFieldsValueRef={customFieldsValueRef}
          />
        );
      },
      [form, onValuesChange, toolName, runbook, customFieldsValueRef],
    );

    useImperativeHandle(ref, () => ({
      getCustomFields: () => ({
        inputCustomFields,
        customFieldState: customFieldStateRef.current,
      }),
    }));

    const collapseProps: any = {
      ...getCollapseProps(tooltip),
      description: (
        <Form
          onValuesChange={onValuesChange}
          form={form}
          layout="vertical"
          initialValues={initialData.current}
        >
          <Spin
            spinning={loading}
            indicator={<AppLoader style={{ marginTop: 0 }} />}
          >
            {_.isEmpty(customField) ? text : renderFields(customField)}
          </Spin>
        </Form>
      ),
      isCollapseDisabled: customField.some((field) => field.requireField),
    };

    return <CustomCollapse {...collapseProps} />;
  },
);

export default ServiceNowCustomFields;
